<template>
  <div ref="modal" class="modal fade" :class="{ show: active, 'd-block': active }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-md" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEdit ? "Edit Company Rental" : "Tambah Company Rental" }}</h5>
          <button type="button" class="btn-close" @click="$emit('closeModal')" aria-label="btn-close"></button>
        </div>

        <form ref="formElement" class="forms-sample" method="POST" @submit.prevent="submitData">
          <div class="modal-body">

            <!-- Company Name (multiselect searchable) -->
            <div class="mb-3">
              <label class="form-label">Company Name <span class="text-danger">*</span></label>
              <multiselect
                v-model="forms.selectedCompany"
                :options="fetchCompany"
                track-by="company_id"
                label="name"
                placeholder="Cari nama company..."
                :searchable="true"
                :loading="isSearching"
                :custom-label="companyLabel"
                @search-change="asyncSearchCompany"
                :show-labels="false"
              >
                <span slot="noResult">Tidak ditemukan</span>
              </multiselect>
            </div>

            <!-- Company Code (auto dari pilihan multiselect) -->
            <div class="mb-3">
              <label class="form-label">Company Code</label>
              <input
                type="text"
                class="form-control"
                :value="forms.selectedCompany ? (forms.selectedCompany.code || forms.selectedCompany.company_code || '') : ''"
                disabled
              />
            </div>

            <!-- SAP Code (auto dari pilihan multiselect) -->
            <div class="mb-3">
              <label class="form-label">SAP Code</label>
              <input
                type="text"
                class="form-control"
                :value="forms.selectedCompany ? (forms.selectedCompany.sap_code || '') : ''"
                disabled
              />
            </div>

            <!-- Enable Pro-Rate -->
            <div class="mb-3">
              <label class="form-label">Enable Rental Pro-Rate</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="checkRentalProrate"
                  v-model="forms.rental_prorate"
                />
                <label class="form-check-label" for="checkRentalProrate">Yes</label>
              </div>
            </div>

          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="$emit('closeModal')">
              Kembali
            </button>

            <div v-if="isLoading" class="d-flex align-items-center">
              <div class="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></div>
              <strong class="ms-2">Menyimpan...</strong>
            </div>

            <button v-if="!isLoading" class="btn btn-success" type="submit">
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import multiselect from "vue-multiselect";

export default {
  name: "ModalProformaRentalCompany",
  components: {
    multiselect,
  },
  emits: ["closeModal", "saved"],

  props: {
    showModal: Boolean,
    // data berisi row yang di-edit (null jika create)
    data: {
      type: Object,
      default: null,
    },
    // endpoint base PI (dari $settings.endPointProformaInvoice)
    piEndpoint: {
      type: String,
      default: "",
    },
  },

  watch: {
    showModal: {
      handler(newVal) {
        this.active = newVal;
        const body = document.querySelector("body");
        newVal
          ? body.classList.add("modal-open")
          : body.classList.remove("modal-open");

        if (newVal) {
          this.resetForms();
          this.loadCompany();

          // Jika mode edit, isi form dari prop data
          if (this.data) {
            this.forms.selectedCompany = {
              company_id:   this.data.company_id,
              name:         this.data.company_name,
              company_name: this.data.company_name,
              code:         this.data.company_code,
              company_code: this.data.company_code,
              sap_code:     this.data.sap_code,
            };
            this.forms.rental_prorate = this.data.rental_prorate === "Y";
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },

  data() {
    return {
      active: this.showModal,
      isLoading: false,
      isSearching: false,
      fetchCompany: [],
      forms: {
        selectedCompany: null,
        rental_prorate: false,
      },
    };
  },

  computed: {
    isEdit() {
      return !!(this.data && this.data.proforma_rental_company_id);
    },
    baseEndpoint() {
      return String(this.piEndpoint || "").replace(/\/?$/, "/");
    },
  },

  methods: {
    resetForms() {
      this.forms = {
        selectedCompany: null,
        rental_prorate: false,
      };
      this.fetchCompany = [];
    },

    companyLabel(option) {
      if (!option) return "";
      const code = option.code || option.company_code || "";
      const name = option.name || option.company_name || "";
      return code && name ? `${code} | ${name}` : name || code;
    },

    loadCompany() {
      const baseURI = this.$settings.endPoint + "company/select?status=active&limit=10";
      this.isSearching = true;
      return this.$http
        .get(baseURI)
        .then((response) => {
          this.fetchCompany = response.data?.datas?.data || [];
        })
        .catch(() => {
          this.fetchCompany = [];
        })
        .finally(() => {
          this.isSearching = false;
        });
    },

    asyncSearchCompany(query) {
      const baseURI =
        this.$settings.endPoint +
        "company/select?status=active&limit=20&name=" +
        (query || "");
      this.isSearching = true;
      return this.$http
        .get(baseURI)
        .then((response) => {
          this.fetchCompany = response.data?.datas?.data || [];
        })
        .catch(() => {
          this.fetchCompany = [];
        })
        .finally(() => {
          this.isSearching = false;
        });
    },

    async submitData() {
      if (!this.forms.selectedCompany) {
        this.showError("Company Name wajib diisi.");
        return;
      }

      this.isLoading = true;
      const payload = {
        company_id:     this.forms.selectedCompany.company_id,
        company_name:   this.forms.selectedCompany.name || this.forms.selectedCompany.company_name,
        company_code:   this.forms.selectedCompany.code || this.forms.selectedCompany.company_code,
        sap_code:       this.forms.selectedCompany.sap_code || "",
        rental_prorate: this.forms.rental_prorate ? "Y" : "N",
      };

      try {
        if (this.isEdit) {
          await this.$http.put(
            `${this.baseEndpoint}v1/proforma-rental-company/update/${this.data.proforma_rental_company_id}`,
            payload
          );
        } else {
          await this.$http.post(
            `${this.baseEndpoint}v1/proforma-rental-company/create`,
            payload
          );
        }

        this.showSuccess(this.isEdit ? "Company berhasil diupdate." : "Company berhasil ditambahkan.");
        this.$emit("saved");
        this.$emit("closeModal");
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleError(error) {
      if (error?.response?.status === 500) {
        this.showError("Server error, coba lagi.");
        return;
      }
      this.showError(
        error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        "Terjadi kesalahan."
      );
    },

    showError(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      Toast.fire({ icon: "error", title: message });
    },

    showSuccess(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      Toast.fire({ icon: "success", title: message });
    },
  },

  mounted() {
    // MutationObserver: fix multiselect readonly — ikuti pola modalAcceptMpr.vue
    const formElement = this.$refs.formElement;
    if (formElement) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "attributes" && mutation.attributeName === "readonly") {
            const inputs = formElement.querySelectorAll(".multiselect__input");
            inputs.forEach((input) => {
              input.removeAttribute("readonly");
            });
          }
        });
      });

      const inputs = formElement.querySelectorAll(".multiselect__input");
      inputs.forEach((input) => {
        observer.observe(input, { attributes: true });
      });
    }
  },
};
</script>

<style scoped>
.modal {
  background: rgba(0, 0, 0, 0.4);
}

.modal-content {
  background: #fff;
  border-radius: 4px;
  border: none;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
}
</style>
